import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { cloneDeep } from 'lodash';

import { calcClientDerivatives, isJointPolicy } from '@shared';
import { FundingPolicyType } from '@shared/appModel';
import { selectClient } from '@client/model/selectors';

import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useSession } from '@/features/guided-financial-planning/context';
import { splitAllocationByPaymentSchedule } from '@/features/guided-financial-planning/utils';
import { useAddPolicyFromQuote } from './use-add-policy-from-quote';
import { usePartnerClient } from './use-partner-client';
import {
  isTraditionalLTCQuote,
  getCarrierName,
  type GuidedPlanningQuote,
} from './use-quotes-display';

/**
 * Selected quote info structure from useAddPolicyFromQuote
 */
interface SelectedQuoteInfo {
  quoteId: string;
  quote: GuidedPlanningQuote;
  policyFundingSource: PendingPolicyFundingSource;
}

/**
 * Metrics for a single scenario (with or without insurance)
 */
export interface ScenarioMetrics {
  /** Self-funding projected coverage */
  projectedAssets: number;
  /** Policy coverage (0 for without insurance scenario) */
  policyCoverage: number;
  /** Combined coverage (self-funding + policy) */
  totalCoverage: number;
  /** Percentage of LTC cost covered */
  coveragePercent: number;
  /** Coverage gap: max(0, ltcCost - totalCoverage) */
  coverageGap: number;
  /** Surplus coverage: max(0, totalCoverage - ltcCost) */
  surplus: number;
  /** Sum of existing assets value (self-funding + policy face value) */
  currentAssets: number;
  /** Combined total value of all funding sources at start of LTC */
  projectedAssetsAtStart: number;
  /** Remaining assets after modeled LTC costs */
  projectedAssetsAtEnd: number;
}

/**
 * Information about the selected policy from the guided planning session
 */
export interface SelectedPolicyInfo {
  quoteId: string;
  productName: string;
  carrierName: string;
  policyType: FundingPolicyType | null;
  totalCost: number;
}

/**
 * Allocation from a single funding source toward the policy
 */
export interface FundingAllocation {
  selfFundingSourceId: string;
  accountName: string;
  amount: number;
  existingValue?: number;
}

/**
 * Benefit scenario analysis - categorizes the type of improvement insurance provides
 */
export interface InsuranceBenefitScenario {
  /** Insurance provides any meaningful benefit */
  insuranceIsBetter: boolean;
  /** Gap Reduction: Had gap without insurance, insurance reduces but doesn't eliminate it */
  hasGapReduction: boolean;
  /** Gap Elimination: Had gap without insurance, insurance eliminates it completely */
  hasGapElimination: boolean;
  /** Surplus Improvement: Already had surplus, insurance increases it further */
  hasSurplusImprovement: boolean;
}

/**
 * Result of comparing scenarios with and without insurance
 */
export interface PlanComparisonResult {
  /** Projected total LTC cost */
  projectedLtcCost: number;
  /** Metrics for self-funding only scenario */
  withoutInsurance: ScenarioMetrics;
  /** Metrics for self-funding + insurance scenario */
  withInsurance: ScenarioMetrics;
  /** Information about the selected policy */
  selectedPolicy: SelectedPolicyInfo | null;
  /** Funding allocations toward the policy */
  fundingAllocations: FundingAllocation[];
  /** Reduction in coverage gap: withoutInsurance.gap - withInsurance.gap */
  gapReduction: number;
  /** Net benefit in coverage */
  netBenefit: number;
  /** Benefit scenario analysis */
  benefitScenario: InsuranceBenefitScenario;
  /** Whether a policy is linked to the session */
  hasPolicy: boolean;
  /** True when the comparison ran in couple-mode (joint policy + partner). */
  isCoupleMode: boolean;
  /**
   * Partner's first name when couple-mode is active. Sourced from the
   * fetched partner client so the comparison label is correct even when
   * the primary's intake survey didn't populate `partnerFirstName`.
   */
  partnerFirstName: string | null;
  /** Loading state from session */
  isLoading: boolean;
}

const DEFAULT_METRICS: ScenarioMetrics = {
  projectedAssets: 0,
  policyCoverage: 0,
  totalCoverage: 0,
  coveragePercent: 0,
  coverageGap: 0,
  surplus: 0,
  currentAssets: 0,
  projectedAssetsAtStart: 0,
  projectedAssetsAtEnd: 0,
};

/**
 * Derive the benefit scenario from a snapshot's metrics.
 * Used when returning cached snapshot data for completed sessions.
 */
function deriveBenefitScenarioFromSnapshot(
  snapshot: PlanComparisonSnapshot,
): InsuranceBenefitScenario {
  const withoutGap = snapshot.withoutInsurance.coverageGap;
  const withGap = snapshot.withInsurance.coverageGap;
  const { gapReduction, netBenefit } = snapshot;

  const insuranceIsBetter = gapReduction > 0 || netBenefit > 0;
  const hasGapReduction = withoutGap > 0 && withGap > 0 && withGap < withoutGap;
  const hasGapElimination = withoutGap > 0 && withGap === 0 && insuranceIsBetter;
  const hasSurplusImprovement = withoutGap === 0 && withGap === 0 && insuranceIsBetter;

  return {
    insuranceIsBetter,
    hasGapReduction,
    hasGapElimination,
    hasSurplusImprovement,
  };
}

/**
 * Sum a numeric field across the partner's non-joint, active policies.
 *
 * Couple-mode aggregation reads the joint policy's coverage/total-value from
 * primary (the engine sums both spouses' contributions onto primary). Partner-
 * owned standalone policies (e.g. a single-life policy the partner had before
 * the joint quote) live only on the partner side and would be dropped if we
 * built totals from primary-only — this helper adds them back.
 */
function sumPartnerNonJointPolicies(
  partner: Client | null,
  getValue: (policy: PolicyFundingSource) => number | null | undefined,
): number {
  if (!partner) return 0;
  let total = 0;
  const policies = partner.multipleFundingSources?.ltcPolicy ?? {};
  for (const policy of Object.values(policies)) {
    if (policy.isDeactivated) continue;
    if (isJointPolicy(policy.policyType, partner)) continue;
    total += getValue(policy) ?? 0;
  }
  return total;
}

function sumActiveSelfFundingAssets(client: Client | null): number {
  let total = 0;
  const sources = client?.multipleFundingSources?.selfFunding ?? {};
  for (const source of Object.values(sources)) {
    if (!source.isDeactivated) {
      total += source.existingAssetsValue ?? 0;
    }
  }
  return total;
}

/**
 * Extract scenario metrics from a calculated client object.
 *
 * In couple-mode (joint policy with partner in scope), `calculatedPartner` is
 * passed in and self-funding aggregates sum across both spouses. The joint
 * policy's coverage on `calculated` already includes partner's portion via the
 * engine's joint-policy logic, so policy fields are read from primary only.
 */
function extractMetrics(
  calculated: Client,
  calculatedPartner: Client | null,
  projectedLtcCost: number,
): ScenarioMetrics {
  const primarySfCoverage =
    calculated.multipleFundingSources?.multipleSelfFundingProjectedCostCoverage ?? 0;
  const partnerSfCoverage =
    calculatedPartner?.multipleFundingSources?.multipleSelfFundingProjectedCostCoverage ?? 0;
  const selfFundingCoverage = primarySfCoverage + partnerSfCoverage;

  // Joint policy coverage on `calculated` already includes partner's portion via
  // the engine's joint-policy logic (see calcFundingSourceDerivatives — joint
  // policies sum projectedCostCoverage + partnerProjectedCostCoverage). Add
  // back partner's non-joint standalone policies, which only live on the
  // partner side.
  const partnerNonJointPolicyCoverage = sumPartnerNonJointPolicies(
    calculatedPartner,
    p => p.projectedCostCoverage,
  );
  const policyCoverage =
    (calculated.multipleFundingSources?.multiplePolicyProjectedCostCoverage ?? 0) +
    partnerNonJointPolicyCoverage;

  // Total coverage uses each client's `combinedProjectedCostCoverage` which
  // already includes self-funding + policy + annuity. Summing both clients'
  // combined values would double-count the joint policy on the primary, so we
  // strip partner policy coverage out of partner's combined and add back only
  // the partner's non-joint policies (the joint policy is already on primary).
  const primaryCombinedCoverage = calculated.fundingSources?.combinedProjectedCostCoverage ?? 0;
  const partnerSelfPlusAnnuity = calculatedPartner
    ? (calculatedPartner.fundingSources?.combinedProjectedCostCoverage ?? 0) -
      (calculatedPartner.multipleFundingSources?.multiplePolicyProjectedCostCoverage ?? 0)
    : 0;
  const totalCoverage =
    primaryCombinedCoverage + partnerSelfPlusAnnuity + partnerNonJointPolicyCoverage;

  // Current assets: sum of existingAssetsValue across active self-funding (both
  // spouses in couple-mode) + policy face values (joint policy face is on primary).
  const currentSelfFundingAssets =
    sumActiveSelfFundingAssets(calculated) + sumActiveSelfFundingAssets(calculatedPartner);

  const policySources = calculated.multipleFundingSources?.ltcPolicy ?? {};
  let currentPolicyBenefit = 0;
  for (const policy of Object.values(policySources)) {
    if (!policy.isDeactivated) {
      currentPolicyBenefit += policy.policyMaximumBenefitAmount ?? 0;
    }
  }
  const currentAssets = currentSelfFundingAssets + currentPolicyBenefit;

  // Projected at start of LTC: self-funding + policy total values (excludes
  // annuities to keep the guided planning comparison scoped to self-funding
  // vs self-funding+insurance).
  const primarySfTotalValue =
    calculated.multipleFundingSources?.multipleSelfFundingTotalValue ?? 0;
  const partnerSfTotalValue =
    calculatedPartner?.multipleFundingSources?.multipleSelfFundingTotalValue ?? 0;
  const selfFundingTotalValue = primarySfTotalValue + partnerSfTotalValue;
  // Joint policy total value is on primary (engine combines both spouses);
  // add back partner's non-joint standalone policies so they aren't dropped.
  const partnerNonJointPolicyTotalValue = sumPartnerNonJointPolicies(
    calculatedPartner,
    p => p.policyTotalValue,
  );
  const policyTotalValue =
    (calculated.multipleFundingSources?.multiplePolicyTotalValue ?? 0) +
    partnerNonJointPolicyTotalValue;
  const relevantTotalValue = selfFundingTotalValue + policyTotalValue;

  // Projected at end of LTC: policy pays first, self-funding covers the remainder.
  // For traditional LTC, unused policy benefit is forfeited (policyNonLtcPayout = 0).
  // For hybrid/life policies, unused benefit converts to a death benefit payout.
  const excessPolicyCoverage = Math.max(0, policyCoverage - projectedLtcCost);
  const policyRemainder =
    (calculated.multipleFundingSources?.multiplePolicyNonLtcPayout ?? 0) + excessPolicyCoverage;
  const costAfterPolicy = Math.max(0, projectedLtcCost - policyCoverage);
  const selfFundingSurplus = Math.max(0, selfFundingTotalValue - costAfterPolicy);
  const projectedAssetsAtEnd = selfFundingSurplus + policyRemainder;

  return {
    projectedAssets: selfFundingCoverage,
    policyCoverage,
    totalCoverage,
    coveragePercent:
      projectedLtcCost > 0 ? Math.round((totalCoverage / projectedLtcCost) * 100) : 0,
    coverageGap: Math.max(0, projectedLtcCost - totalCoverage),
    surplus: Math.max(0, totalCoverage - projectedLtcCost),
    currentAssets,
    projectedAssetsAtStart: relevantTotalValue,
    projectedAssetsAtEnd,
  };
}

/**
 * Build a partner clone for couple-mode comparisons. Clears existing policies
 * when the user didn't tick "Existing LTC" on the assets step (matches the
 * primary clone's logic so "Without Insurance" represents pure self-funding
 * for both spouses).
 *
 * Also hydrates `clientBirthDate` to a Date on every survey that carries it.
 * The engine reassigns `mutableClient.intakeSurvey = surveys.find(...)`
 * inside `calcSurveyDerivatives`, so hydrating only `intakeSurvey` is
 * insufficient — the survey object inside `surveys[]` is what the engine
 * actually reads. Partner clients fetched via React Query may surface the
 * date as an ISO string depending on which consumer primed the shared cache
 * first.
 */
function buildPartnerCloneForCoupleMode(
  partnerClient: Client | null,
  userSelectedExistingLtcPolicy: boolean,
): Client | null {
  if (!partnerClient) return null;
  const partnerClone = cloneDeep(partnerClient);

  const hydrateBirthDate = (target: { clientBirthDate?: Date | string | null }): void => {
    const birthDate = target.clientBirthDate;
    if (birthDate != null && !(birthDate instanceof Date)) {
      target.clientBirthDate = new Date(birthDate as unknown as string);
    }
  };
  if (partnerClone.intakeSurvey) hydrateBirthDate(partnerClone.intakeSurvey);
  if (Array.isArray(partnerClone.surveys)) {
    for (const survey of partnerClone.surveys) {
      if (survey) hydrateBirthDate(survey as { clientBirthDate?: Date | string | null });
    }
  }

  if (!userSelectedExistingLtcPolicy && partnerClone.multipleFundingSources?.ltcPolicy) {
    partnerClone.multipleFundingSources.ltcPolicy = {};
  }
  return partnerClone;
}

/**
 * Helper to calculate all plan comparison data from client and session state.
 * When user did not select "Existing LTC" on assets step, the "Without Insurance"
 * scenario uses self-funding only (existing LTC policies excluded), matching Coverage Assessment.
 *
 * For joint policies with a partner client available, runs in "couple-mode":
 * passes partner to `calcClientDerivatives` so the engine activates joint
 * policy coverage logic, and combines `projectedLtcCost` plus self-funding
 * aggregates across both spouses.
 */
function calculatePlanComparison(
  client: Client | null,
  partnerClient: Client | null,
  selectedQuoteInfo: SelectedQuoteInfo | null,
  allocations: Record<string, number>,
  userSelectedExistingLtcPolicy: boolean,
): Omit<PlanComparisonResult, 'isLoading'> {
  const useCoupleMode = !!(
    selectedQuoteInfo &&
    partnerClient &&
    isJointPolicy(selectedQuoteInfo.policyFundingSource.policyType, partnerClient)
  );

  // Derive partner once up front so we have a fresh, populated `allPhaseCosts`
  // for the cost calculation and a derived partner client to seed each
  // scenario. Partner clients fetched via React Query don't have derivatives
  // populated (only `clientBirthDate` is hydrated — see `usePartnerClient`),
  // so reading `partnerClient.allPhaseCosts` directly returns whatever the API
  // last serialized, which is stale or missing.
  const partnerCloneBase = useCoupleMode
    ? buildPartnerCloneForCoupleMode(partnerClient, userSelectedExistingLtcPolicy)
    : null;
  const calculatedPartnerBase = partnerCloneBase
    ? calcClientDerivatives(partnerCloneBase, null)
    : null;

  const primaryLtcCost = client?.allPhaseCosts?.allPhaseInflatedProfessionalShareCost ?? 0;
  const partnerLtcCost =
    calculatedPartnerBase?.allPhaseCosts?.allPhaseInflatedProfessionalShareCost ?? 0;
  const projectedLtcCost = primaryLtcCost + partnerLtcCost;

  // Each scenario gets its own deep clone of the derived partner. The engine's
  // joint-policy logic mutates partner via `copyJointPolicyToClientPartner`,
  // so sharing a single instance across scenarios would cross-contaminate.
  const buildScenarioPartner = (): Client | null =>
    calculatedPartnerBase ? cloneDeep(calculatedPartnerBase) : null;

  // Calculate "Without Insurance" scenario
  let withoutInsurance = DEFAULT_METRICS;
  if (client) {
    const clientClone = cloneDeep(client);
    if (!userSelectedExistingLtcPolicy && clientClone.multipleFundingSources?.ltcPolicy) {
      clientClone.multipleFundingSources.ltcPolicy = {};
    }
    const calculatedPartner = buildScenarioPartner();
    const calculated = calcClientDerivatives(clientClone, calculatedPartner);
    withoutInsurance = extractMetrics(calculated, calculatedPartner, projectedLtcCost);
  }

  // Calculate "With Insurance" scenario
  let withInsurance = withoutInsurance;
  let selectedPolicy: SelectedPolicyInfo | null = null;

  if (client && selectedQuoteInfo) {
    const clientClone = cloneDeep(client);
    if (!userSelectedExistingLtcPolicy && clientClone.multipleFundingSources?.ltcPolicy) {
      clientClone.multipleFundingSources.ltcPolicy = {};
    }

    // Inject the policy from stepData into the client clone
    const tempFundingSourceId = selectedQuoteInfo.policyFundingSource.pendingFundingSourceId;
    if (!clientClone.multipleFundingSources) {
      clientClone.multipleFundingSources = {
        selfFunding: {},
        ltcPolicy: {},
        annuity: {},
      };
    }
    if (!clientClone.multipleFundingSources.ltcPolicy) {
      clientClone.multipleFundingSources.ltcPolicy = {};
    }
    clientClone.multipleFundingSources.ltcPolicy[tempFundingSourceId] =
      selectedQuoteInfo.policyFundingSource;

    // Apply premium cash flow to each funded source. The lump-sum portion is
    // a year-0 deduction from existingAssetsValue; the recurring portion is
    // layered on as an additional drawdown so the engine subtracts each
    // year's premium share while the rest of the principal keeps compounding.
    const ltcAtYear = clientClone.appliedInferenceSet?.ltcAtYear ?? null;
    Object.entries(allocations).forEach(([selfFundingSourceId, amount]) => {
      const source = clientClone.multipleFundingSources?.selfFunding?.[selfFundingSourceId];
      if (!source || source.existingAssetsValue == null) return;

      const { lumpSumDeduction, recurringDrawdowns } = splitAllocationByPaymentSchedule({
        allocatedAmount: amount,
        policy: selectedQuoteInfo.policyFundingSource,
        ltcAtYear,
      });

      if (lumpSumDeduction > 0) {
        source.existingAssetsValue = Math.max(0, source.existingAssetsValue - lumpSumDeduction);
      }
      if (recurringDrawdowns.length > 0) {
        source.additionalDrawdowns = [
          ...(source.additionalDrawdowns ?? []),
          ...recurringDrawdowns,
        ];
      }
    });

    // For joint policies, pass partner to the engine so joint coverage logic
    // activates (`copyJointPolicyToClientPartner`,
    // `combineSortClientPartnerPhaseCosts`). Premium drawdowns stay on primary
    // sources only — the wizard models primary funding the joint policy.
    const calculatedPartner = buildScenarioPartner();
    const calculated = calcClientDerivatives(clientClone, calculatedPartner);
    withInsurance = extractMetrics(calculated, calculatedPartner, projectedLtcCost);
    // Build selected policy info with calculated policyTotalCost
    const { quoteId, quote, policyFundingSource } = selectedQuoteInfo;
    const calculatedPolicy = calculated.multipleFundingSources?.ltcPolicy?.[tempFundingSourceId];
    selectedPolicy = {
      quoteId,
      productName: quote.productName ?? 'Selected Policy',
      carrierName: isTraditionalLTCQuote(quote) ? quote.carrier : getCarrierName(quote),
      policyType: policyFundingSource.policyType ?? null,
      totalCost: calculatedPolicy?.policyTotalCost ?? 0,
    };
  }

  // Build funding allocations list
  const selfFunding = client?.multipleFundingSources?.selfFunding ?? {};
  const fundingAllocations: FundingAllocation[] = Object.entries(allocations)
    .filter(([, amount]) => amount > 0)
    .map(([selfFundingSourceId, amount]) => {
      const source = selfFunding[selfFundingSourceId];
      return {
        selfFundingSourceId,
        accountName: source?.selfFundingName ?? source?.accountType ?? 'Unknown Account',
        amount,
        existingValue: source?.existingAssetsValue ?? 0,
      };
    });

  // Calculate comparison values — use projected assets remaining after LTC
  // so the banner matches the bottom-line values shown on each card.
  const gapReduction = withoutInsurance.coverageGap - withInsurance.coverageGap;
  const netBenefit = withInsurance.projectedAssetsAtEnd - withoutInsurance.projectedAssetsAtEnd;

  // Calculate benefit scenario
  const insuranceIsBetter = netBenefit > 0;

  const withoutGap = withoutInsurance.coverageGap;
  const withGap = withInsurance.coverageGap;

  const benefitScenario: InsuranceBenefitScenario = {
    insuranceIsBetter,
    hasGapReduction: withoutGap > 0 && withGap > 0 && withGap < withoutGap,
    hasGapElimination: withoutGap > 0 && withGap === 0 && insuranceIsBetter,
    hasSurplusImprovement: withoutGap === 0 && withGap === 0 && insuranceIsBetter,
  };

  return {
    projectedLtcCost,
    withoutInsurance,
    withInsurance,
    selectedPolicy,
    fundingAllocations,
    gapReduction,
    netBenefit,
    benefitScenario,
    hasPolicy: selectedQuoteInfo !== null,
    isCoupleMode: useCoupleMode,
    partnerFirstName: useCoupleMode ? partnerClient?.clientFirstName ?? null : null,
  };
}

/**
 * Hook that compares two scenarios for the plan step:
 * 1. Without Insurance - Self-funding only
 * 2. With Insurance - Self-funding (minus premium allocations) + session policy
 *
 * Reads the selected quote from session.stepData.selectedQuotes and injects
 * the policy into calculations without requiring database persistence.
 *
 * For completed sessions, returns the cached snapshot to ensure consistent
 * read-only display after finalization.
 */
export function usePlanComparisonScenarios(): PlanComparisonResult {
  const client = useSelector(selectClient);
  const { session, isLoading } = useSession();
  const { flags } = useFeatureFlags();
  const { selectedQuote: selectedQuoteInfo } = useAddPolicyFromQuote();
  const { partnerClient } = usePartnerClient();

  // Check for cached snapshot (used for completed sessions)
  const snapshot = session?.stepData?.planComparisonSnapshot;

  const userSelectedExistingLtcPolicy = Boolean(
    flags.existingLtcPolicyTypeOnCard &&
    (session?.stepData?.selectedAssets ?? []).includes('existing-ltc-policy'),
  );

  // Get allocations for the selected quote from stepData
  const allocations = useMemo(() => {
    if (!selectedQuoteInfo) return {};
    return (session?.stepData?.policyFundingAllocations?.[selectedQuoteInfo.quoteId] ??
      {}) as Record<string, number>;
  }, [session?.stepData?.policyFundingAllocations, selectedQuoteInfo]);

  // The multipleFundingSourcesSnapshot preserves the original funding sources
  // from before finalization modified them (deductions, policy persistence).
  // Used to recalculate new display metrics for old completed sessions.
  const mfsSnapshot = session?.stepData?.multipleFundingSourcesSnapshot;

  // Calculate all metrics (or return snapshot for completed sessions)
  const result = useMemo((): PlanComparisonResult => {
    // For completed sessions with a snapshot, return the cached data.
    if (session?.status === 'completed' && snapshot) {
      const snapshotHasNewFields = typeof snapshot.withoutInsurance.currentAssets === 'number';

      let withoutInsurance: ScenarioMetrics;
      let withInsurance: ScenarioMetrics;

      if (snapshotHasNewFields) {
        // New snapshot format — all fields present
        withoutInsurance = snapshot.withoutInsurance as ScenarioMetrics;
        withInsurance = snapshot.withInsurance as ScenarioMetrics;
      } else if (mfsSnapshot && client) {
        // Old snapshot: recalculate new fields using the original funding sources
        const tempClient = cloneDeep(client);
        tempClient.multipleFundingSources = mfsSnapshot;
        const recalculated = calculatePlanComparison(
          tempClient,
          partnerClient,
          selectedQuoteInfo,
          allocations,
          userSelectedExistingLtcPolicy,
        );

        withoutInsurance = {
          ...DEFAULT_METRICS,
          ...snapshot.withoutInsurance,
          currentAssets: recalculated.withoutInsurance.currentAssets,
          projectedAssetsAtStart: recalculated.withoutInsurance.projectedAssetsAtStart,
          projectedAssetsAtEnd: recalculated.withoutInsurance.projectedAssetsAtEnd,
        };
        withInsurance = {
          ...DEFAULT_METRICS,
          ...snapshot.withInsurance,
          currentAssets: recalculated.withInsurance.currentAssets,
          projectedAssetsAtStart: recalculated.withInsurance.projectedAssetsAtStart,
          projectedAssetsAtEnd: recalculated.withInsurance.projectedAssetsAtEnd,
        };
      } else {
        // Final fallback: no mfsSnapshot available, use zero defaults
        withoutInsurance = { ...DEFAULT_METRICS, ...snapshot.withoutInsurance };
        withInsurance = { ...DEFAULT_METRICS, ...snapshot.withInsurance };
      }

      // Re-derive couple-mode for completed sessions from the snapshot's
      // policy + currently-fetched partner client. (Snapshot doesn't carry
      // these flags directly, so we recompute the same way as the live path.)
      const completedSessionIsCoupleMode = !!(
        snapshot.selectedPolicy?.policyType &&
        partnerClient &&
        isJointPolicy(snapshot.selectedPolicy.policyType, partnerClient)
      );

      return {
        projectedLtcCost: snapshot.projectedLtcCost,
        withoutInsurance,
        withInsurance,
        selectedPolicy: snapshot.selectedPolicy,
        fundingAllocations: snapshot.fundingAllocations,
        gapReduction: snapshot.gapReduction,
        netBenefit: snapshot.netBenefit,
        benefitScenario: deriveBenefitScenarioFromSnapshot(snapshot),
        hasPolicy: snapshot.selectedPolicy !== null,
        isCoupleMode: completedSessionIsCoupleMode,
        partnerFirstName: completedSessionIsCoupleMode
          ? partnerClient?.clientFirstName ?? null
          : null,
        isLoading: false,
      };
    }

    // Calculate fresh metrics
    return {
      ...calculatePlanComparison(
        client,
        partnerClient,
        selectedQuoteInfo,
        allocations,
        userSelectedExistingLtcPolicy,
      ),
      isLoading,
    };
  }, [
    session?.status,
    snapshot,
    mfsSnapshot,
    session?.stepData?.selectedAssets,
    client,
    partnerClient,
    selectedQuoteInfo,
    allocations,
    userSelectedExistingLtcPolicy,
    flags.existingLtcPolicyTypeOnCard,
    isLoading,
  ]);

  return result;
}
